// Offres « Potential clients » : modèles par langue et rendu du message.
//
// L'aperçu et l'envoi passent par la MÊME fonction renderOffer() : ce qu'on relit à
// l'écran est exactement ce qui part. Le serveur ne fait qu'expédier et journaliser.
//
// Le prix vient de XYLO-PRICING dans la zone (incoterm) du client. Deux règles :
//   - on l'annonce dans l'unité du tarif (pièce, rouleau, m², ml, m³) — jamais converti ;
//   - un prix rendu (DAP/BENELUX) est un prix « camion complet » : on le dit au client,
//     sinon une commande d'une palette le prend au mot.

export const LANGS = ['fr', 'en', 'de', 'nl', 'es', 'ro']
export const LANG_LABELS = { fr: 'Français', en: 'English', de: 'Deutsch', nl: 'Nederlands', es: 'Español', ro: 'Română' }
const LOCALES = { fr: 'fr-FR', en: 'en-GB', de: 'de-DE', nl: 'nl-NL', es: 'es-ES', ro: 'ro-RO' }

// Marque commerciale par langue (mêmes libellés que les devis).
const BRAND = { fr: 'Acacia Bois', en: 'Acacia Poles', de: 'Robinienpfähle', nl: 'Robinia Palen', es: 'Estacas de Acacia', ro: 'Stalpi Salcam' }

// Signaux « a déjà montré de l'intérêt » vs « suggestion » : l'accroche change.
export const INTEREST_REASONS = ['bought', 'quoted']

const UNIT = {
  piece: { fr: 'pièce', en: 'piece', de: 'Stück', nl: 'stuk', es: 'unidad', ro: 'bucată' },
  roll: { fr: 'rouleau', en: 'roll', de: 'Rolle', nl: 'rol', es: 'rollo', ro: 'rolă' },
  linear_m: { fr: 'mètre linéaire', en: 'linear metre', de: 'laufenden Meter', nl: 'strekkende meter', es: 'metro lineal', ro: 'metru liniar' },
  m2: { fr: 'm²', en: 'm²', de: 'm²', nl: 'm²', es: 'm²', ro: 'm²' },
  m3: { fr: 'm³', en: 'm³', de: 'm³', nl: 'm³', es: 'm³', ro: 'm³' }
}
const UNIT_PLURAL = {
  piece: { fr: 'pièces', en: 'pieces', de: 'Stück', nl: 'stuks', es: 'unidades', ro: 'bucăți' },
  roll: { fr: 'rouleaux', en: 'rolls', de: 'Rollen', nl: 'rollen', es: 'rollos', ro: 'role' }
}
const ROLL_OF = { fr: 'rouleau de', en: 'roll of', de: 'Rolle à', nl: 'rol van', es: 'rollo de', ro: 'rolă de' }
const EXCL_VAT = { fr: 'HT', en: 'excl. VAT', de: 'zzgl. MwSt.', nl: 'excl. btw', es: 'sin IVA', ro: 'fără TVA' }
const INCOTERM = {
  exw: {
    fr: 'départ usine (EXW)',
    en: 'ex works (EXW)',
    de: 'ab Werk (EXW)',
    nl: 'af fabriek (EXW)',
    es: 'en fábrica (EXW)',
    ro: 'franco fabrică (EXW)'
  },
  delivered: {
    fr: 'livraison comprise (DAP), sur la base d\'un camion complet',
    en: 'delivered (DAP), based on a full truckload',
    de: 'geliefert (DAP), bei Abnahme einer vollen LKW-Ladung',
    nl: 'geleverd (DAP), op basis van een volle vrachtwagen',
    es: 'entregado en destino (DAP), por camión completo',
    ro: 'livrat la destinație (DAP), pentru un camion complet'
  }
}
const PACKAGING = {
  fr: (n, u) => `Conditionnement : ${n} ${u} par palette.`,
  en: (n, u) => `Packaging: ${n} ${u} per pallet.`,
  de: (n, u) => `Verpackung: ${n} ${u} pro Palette.`,
  nl: (n, u) => `Verpakking: ${n} ${u} per pallet.`,
  es: (n, u) => `Embalaje: ${n} ${u} por palé.`,
  ro: (n, u) => `Ambalare: ${n} ${u} pe palet.`
}
const HELLO = {
  fr: n => (n ? `Bonjour ${n},` : 'Bonjour,'),
  en: n => (n ? `Dear ${n},` : 'Hello,'),
  de: n => (n ? `Guten Tag ${n},` : 'Guten Tag,'),
  nl: n => (n ? `Beste ${n},` : 'Beste,'),
  es: n => (n ? `Estimado/a ${n}:` : 'Buenos días:'),
  ro: n => (n ? `Bună ziua ${n},` : 'Bună ziua,')
}
const GOODBYE = {
  fr: 'Cordialement,<br/>L\'équipe commerciale<br/>Acacia Bois / Xylo Impact',
  en: 'Kind regards,<br/>The sales team<br/>Acacia Poles / Xylo Impact',
  de: 'mfG,<br/>Das Verkaufsteam<br/>Robinienpfähle / Xylo Impact',
  nl: 'Met vriendelijke groeten,<br/>Het verkoopteam<br/>Robinia Palen / Xylo Impact',
  es: 'Atentamente,<br/>el equipo comercial<br/>Estacas de Acacia / Xylo Impact',
  ro: 'Cu stima,<br/>Echipa de vânzări<br/>Stalpi Salcam / Xylo Impact'
}

export const DEFAULT_TEMPLATES = {
  fr: {
    subject: `Offre : {{product}} – ${BRAND.fr}`,
    intro_interest: 'Vous aviez montré de l\'intérêt pour notre produit {{product}}.',
    intro_suggestion: 'Au vu de vos commandes précédentes, notre produit {{product}} pourrait vous intéresser.',
    body: '{{intro}}\nNous l\'avons actuellement en stock et nous aimerions vous le proposer au prix de {{price}}, {{incoterm}}.\n{{packaging}}\n\nCette offre est valable dans la limite des stocks disponibles. Il vous suffit de répondre à ce message pour passer commande ou pour toute question.',
    optout: 'Vous ne souhaitez plus recevoir nos offres ? Répondez simplement « STOP » à ce message.'
  },
  en: {
    subject: `Offer: {{product}} – ${BRAND.en}`,
    intro_interest: 'You previously showed interest in our product {{product}}.',
    intro_suggestion: 'Based on your previous orders, our product {{product}} may be of interest to you.',
    body: '{{intro}}\nWe currently have it in stock and would like to offer it to you at {{price}}, {{incoterm}}.\n{{packaging}}\n\nThis offer is valid while stocks last. Simply reply to this e-mail to place an order or if you have any questions.',
    optout: 'Prefer not to receive our offers? Simply reply "STOP" to this message.'
  },
  de: {
    subject: `Angebot: {{product}} – ${BRAND.de}`,
    intro_interest: 'Sie hatten Interesse an unserem Produkt {{product}} gezeigt.',
    intro_suggestion: 'Aufgrund Ihrer bisherigen Bestellungen könnte Sie unser Produkt {{product}} interessieren.',
    body: '{{intro}}\nWir haben es derzeit auf Lager und möchten es Ihnen zum Preis von {{price}} anbieten, {{incoterm}}.\n{{packaging}}\n\nDieses Angebot gilt, solange der Vorrat reicht. Antworten Sie einfach auf diese E-Mail, um zu bestellen oder wenn Sie Fragen haben.',
    optout: 'Sie möchten keine Angebote mehr erhalten? Antworten Sie einfach mit „STOP“.'
  },
  nl: {
    subject: `Aanbieding: {{product}} – ${BRAND.nl}`,
    intro_interest: 'U toonde eerder interesse in ons product {{product}}.',
    intro_suggestion: 'Op basis van uw eerdere bestellingen zou ons product {{product}} u kunnen interesseren.',
    body: '{{intro}}\nWe hebben het momenteel op voorraad en bieden het u graag aan tegen {{price}}, {{incoterm}}.\n{{packaging}}\n\nDeze aanbieding is geldig zolang de voorraad strekt. Beantwoord gewoon deze e-mail om te bestellen of als u vragen heeft.',
    optout: 'Wilt u geen aanbiedingen meer ontvangen? Antwoord dan gewoon met "STOP".'
  },
  es: {
    subject: `Oferta: {{product}} – ${BRAND.es}`,
    intro_interest: 'Usted mostró interés por nuestro producto {{product}}.',
    intro_suggestion: 'Teniendo en cuenta sus pedidos anteriores, nuestro producto {{product}} podría interesarle.',
    body: '{{intro}}\nActualmente lo tenemos en stock y nos gustaría ofrecérselo al precio de {{price}}, {{incoterm}}.\n{{packaging}}\n\nEsta oferta es válida hasta agotar existencias. Basta con responder a este correo para hacer un pedido o para cualquier consulta.',
    optout: '¿Prefiere no recibir nuestras ofertas? Responda simplemente «STOP» a este mensaje.'
  },
  ro: {
    subject: `Ofertă: {{product}} – ${BRAND.ro}`,
    intro_interest: 'Ați manifestat interes pentru produsul nostru {{product}}.',
    intro_suggestion: 'Având în vedere comenzile dumneavoastră anterioare, produsul nostru {{product}} v-ar putea interesa.',
    body: '{{intro}}\nÎl avem în prezent pe stoc și am dori să vi-l oferim la prețul de {{price}}, {{incoterm}}.\n{{packaging}}\n\nOferta este valabilă în limita stocului disponibil. Este suficient să răspundeți la acest e-mail pentru a plasa o comandă sau pentru orice întrebare.',
    optout: 'Nu mai doriți să primiți ofertele noastre? Răspundeți simplu cu „STOP” la acest mesaj.'
  }
}

export function cloneDefaults () {
  return JSON.parse(JSON.stringify(DEFAULT_TEMPLATES))
}

export function escapeHtml (value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function normLang (lang) {
  const l = String(lang || '').slice(0, 2).toLowerCase()
  return LANGS.indexOf(l) !== -1 ? l : 'en'
}

export function formatMoney (amount, lang) {
  const n = Number(amount)
  if (!Number.isFinite(n)) return ''
  try {
    return new Intl.NumberFormat(LOCALES[normLang(lang)], { style: 'currency', currency: 'EUR' }).format(n)
  } catch (e) {
    return `${n.toFixed(2)} €`
  }
}

// « 126,80 € HT / rouleau de 10 m »
export function priceText (amount, unit, lang, rollLength) {
  const l = normLang(lang)
  const u = unit || 'piece'
  let per
  if (u === 'roll' && rollLength) per = `${ROLL_OF[l]} ${String(rollLength).replace('.', l === 'en' ? '.' : ',')} m`
  else per = (UNIT[u] && UNIT[u][l]) || u
  return `${formatMoney(amount, l)} ${EXCL_VAT[l]} / ${per}`
}

export function incotermText (delivered, lang) {
  return INCOTERM[delivered ? 'delivered' : 'exw'][normLang(lang)]
}

export function packagingText (piecesPerPallet, unit, lang) {
  const n = Number(piecesPerPallet)
  if (!(n > 0)) return ''
  const l = normLang(lang)
  const plural = UNIT_PLURAL[unit === 'roll' ? 'roll' : 'piece'][l]
  return PACKAGING[l](n, plural)
}

export function productText (labels, lang) {
  const l = normLang(lang)
  const lab = (labels && (labels[l] || labels.en)) || {}
  return [lab.category, lab.name].filter(Boolean).join(' ').trim()
}

// Un particulier est salué par son nom ; une société, sans (« Bonjour Ernest Roth SA, » sonne faux).
export function helloText (person, lang) {
  const l = normLang(lang)
  const name = person && person.iscompany === false
    ? [person.firstname, person.lastname].filter(Boolean).join(' ').trim()
    : ''
  return HELLO[l](name)
}

export function goodbyeHtml (lang) {
  return GOODBYE[normLang(lang)]
}

// Remplit {{variable}} en texte brut (sujet, contrôle des lignes vides).
function fillText (text, vars) {
  return String(text || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (m, key) => {
    if (!(key in vars)) return m
    return vars[key] == null ? '' : String(vars[key])
  })
}

// Même chose en HTML : le texte du modèle ET les valeurs sont échappés (le corps est du
// texte brut éditable, jamais du HTML) ; produit et prix en gras ; `raw` = HTML déjà sûr.
function fillHtml (text, vars, raw = {}) {
  const src = String(text || '')
  const re = /\{\{\s*(\w+)\s*\}\}/g
  let out = ''
  let last = 0
  let m
  while ((m = re.exec(src))) {
    out += escapeHtml(src.slice(last, m.index))
    const key = m[1]
    if (key in raw) out += raw[key]
    else if (key in vars) {
      const v = vars[key] == null ? '' : String(vars[key])
      out += key === 'price' || key === 'product' ? `<b>${escapeHtml(v)}</b>` : escapeHtml(v)
    } else out += escapeHtml(m[0])
    last = re.lastIndex
  }
  return out + escapeHtml(src.slice(last))
}

/**
 * Rend l'offre d'un destinataire.
 * @param {object} tpl      modèle de la langue { subject, intro_interest, intro_suggestion, body, optout }
 * @param {object} ctx      { lang, person, reason, labels, price: { amount, unit, roll_length_m, delivered, pieces_per_pallet } }
 * @returns {{ subject, hello, message_html, goodbye_html }}
 */
export function renderOffer (tpl, ctx) {
  const lang = normLang(ctx.lang)
  const t = tpl || DEFAULT_TEMPLATES[lang]
  const p = ctx.price || {}
  const base = {
    product: productText(ctx.labels, lang),
    price: p.amount != null ? priceText(p.amount, p.unit, lang, p.roll_length_m) : '',
    incoterm: incotermText(p.delivered, lang),
    packaging: packagingText(p.pieces_per_pallet, p.unit, lang)
  }
  const introTpl = INTEREST_REASONS.indexOf(ctx.reason) !== -1 ? t.intro_interest : t.intro_suggestion
  const plainVars = Object.assign({}, base, { intro: fillText(introTpl, base) })
  const introHtml = fillHtml(introTpl, base)

  // Ligne par ligne : une ligne qui ne contenait qu'une variable vide (conditionnement
  // inconnu…) disparaît au lieu de laisser un blanc.
  const out = []
  for (const line of String(t.body || '').split(/\r?\n/)) {
    const hadVar = /\{\{\s*\w+\s*\}\}/.test(line)
    const plain = fillText(line, plainVars).trim()
    if (hadVar && !plain) continue
    out.push(plain ? fillHtml(line, base, { intro: introHtml }) : '')
  }
  let html = out.join('<br/>').replace(/(<br\/>){3,}/g, '<br/><br/>')
  if (t.optout && String(t.optout).trim()) {
    html += `<br/><br/><span style="font-size:11px;color:#888;">${escapeHtml(t.optout)}</span>`
  }
  return {
    subject: fillText(t.subject, plainVars).replace(/\s+/g, ' ').trim(),
    hello: helloText(ctx.person, lang),
    message_html: html,
    goodbye_html: goodbyeHtml(lang)
  }
}

// Les boîtes de facturation ne sont pas la bonne porte pour une offre commerciale.
const ACCOUNTING_BOX = /^(invoice|invoices|facture|factures|facturation|factuur|facturen|factura|facturi|compta|comptabilite|comptabilité|accounting|accounts|billing|rechnung|rechnungen|buchhaltung|boekhouding|contabilitate|contabilidad|payable|ap)[._-]?[^@]*@/i

// Adresses cochées par défaut : les générales (ou mixtes), hors boîtes comptables ;
// à défaut, toutes celles de la fiche.
export function defaultRecipients (emails) {
  const list = Array.isArray(emails) ? emails : []
  const general = list.filter(e => e.source === 'general' || e.source === 'both')
  const pool = general.length ? general : list
  const commercial = pool.filter(e => !ACCOUNTING_BOX.test(e.email))
  return (commercial.length ? commercial : pool).map(e => e.email)
}

export const REASON_LABELS = {
  bought: 'Bought',
  quoted: 'Quoted, not ordered',
  related: 'Bought together',
  similar: 'Similar product',
  area: 'Same area'
}
